"use client";

import { Popover as PopoverPrimitive } from "@base-ui/react/popover";

import { cx } from "../../cx";
import styles from "./popover.module.css";

type WithClassName<P> = Omit<P, "className"> & { className?: string };

function Popover(props: PopoverPrimitive.Root.Props) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger(props: PopoverPrimitive.Trigger.Props) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

const KEEP_FOCUS_OUTSIDE_POPUP = () => false;

type PopoverContentProps = WithClassName<PopoverPrimitive.Popup.Props> &
  Pick<
    PopoverPrimitive.Positioner.Props,
    "side" | "align" | "sideOffset" | "alignOffset" | "collisionAvoidance"
  > & {
    container?: HTMLElement | null;
    /** Radix-era hook: passing it keeps focus on the trigger when opening. */
    onOpenAutoFocus?: (event: Event) => void;
  };

function PopoverContent({
  className,
  align = "start",
  side = "bottom",
  sideOffset = 8,
  alignOffset = 0,
  collisionAvoidance,
  container,
  onOpenAutoFocus,
  initialFocus,
  ...props
}: PopoverContentProps) {
  return (
    <PopoverPrimitive.Portal container={container ?? undefined}>
      <PopoverPrimitive.Positioner
        data-slot="popover-positioner"
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        collisionAvoidance={collisionAvoidance}
        className={styles.positioner}
      >
        <PopoverPrimitive.Popup
          data-tui=""
          data-slot="popover-content"
          initialFocus={
            onOpenAutoFocus ? KEEP_FOCUS_OUTSIDE_POPUP : initialFocus
          }
          className={cx(styles.popup, className)}
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}

function PopoverTitle({
  className,
  ...props
}: WithClassName<PopoverPrimitive.Title.Props>) {
  return (
    <PopoverPrimitive.Title
      data-slot="popover-title"
      className={cx(styles.title, className)}
      {...props}
    />
  );
}

export { Popover, PopoverContent, PopoverTitle, PopoverTrigger };
